import {
  Alert,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Group,
  Stack,
} from "@assetwatch/ui";
import { useState } from "react";
import { getAssetPickerLocationName } from "../lib/assetPickerTree";
import { parseAssigneeValue } from "../lib/assigneeTokens";
import { toOriginSearch } from "../lib/navigationOrigin";
import type { RequestsPageView } from "../lib/requestsPageView";
import type { AssetId, WorkRequestId } from "../lib/workIds";
import { workOrderIdSchema } from "../lib/workIds";
import { getWorkScopeSearch } from "../lib/workSearch";
import { useApproveWorkRequestMutation } from "../mutations/useApproveWorkRequestMutation";
import { useDeclineWorkRequestMutation } from "../mutations/useWorkRequestMutations";
import { useAssetPickerQuery } from "../queries/useAssetPickerQuery";
import { useAssetTreeQuery } from "../queries/useAssetTreeQuery";
import { attachProcedureToWorkOrder } from "../queries/useProcedureAttachment";
import { useProceduresQuery } from "../queries/useProceduresQuery";
import { useTeamsUsersQuery } from "../queries/useTeamsUsersQuery";
import { useWorkOrderLookupsQuery } from "../queries/useWorkOrderLookupsQuery";
import { useWorkRequestApprovalQuery } from "../queries/useWorkRequestApprovalQuery";
import type { RequestPriority, WorkRequest } from "../types/workRequest";
import type { WorkRequestApproval } from "../types/workRequestApi";
import { ApproveWorkOrderForm } from "./ApproveWorkOrderForm";
import {
  type ApprovalAssetLink,
  ApproveWorkRequestSummary,
} from "./ApproveWorkRequestSummary";
import { getProcedureOptions } from "./procedureOptions";
import {
  findAssetIdByTreeId,
  flattenAssetTree,
} from "./snowflakes/assets/assetModel";
import {
  DeclineRequestDialog,
  type DeclineRequestDialogInput,
} from "./snowflakes/requests/DeclineRequestDialog";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { useDraftProcedureSelection } from "./useDraftProcedureSelection";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import { useWorkScope } from "./WorkScopeProvider";

type Props = {
  initialRequestId?: WorkRequestId | null;
  initialRequest?: WorkRequest;
  open: boolean;
  onClose: () => void;
  /** The Requests list view, restored by the asset page's back link. */
  originListSearch?: RequestsPageView;
};
function toApprovalRequest(request: WorkRequest): WorkRequestApproval {
  return {
    id: request.id,
    title: request.title,
    description: request.description ?? "",
    severity: request.priority?.code ?? null,
    status: request.status.code,
    submittedAt: request.submittedAt,
    submittedBy: request.requester.name,
    facilityId: null,
    asset: request.asset
      ? {
          id: request.asset.id,
          code: request.asset.assetCode ?? null,
          name: request.asset.name,
          locationName: request.asset.locationName,
        }
      : { id: 0, code: null, name: "Unassigned", locationName: null },
    convertedWorkOrderId: null,
  };
}
export function ApproveWorkRequestModal({
  initialRequestId = null,
  initialRequest,
  open,
  onClose,
  originListSearch,
}: Props) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const pickerQuery = useAssetPickerQuery();
  const assetTreeQuery = useAssetTreeQuery();
  const { data: lookupData } = useWorkOrderLookupsQuery();
  const { data: teamsUsersData } = useTeamsUsersQuery();
  const procedureQuery = useProceduresQuery("");
  const requestId = initialRequest?.id ?? initialRequestId;
  const approval = useWorkRequestApprovalQuery(
    initialRequest ? null : requestId,
  );
  const approvalData =
    initialRequest?.id === requestId
      ? toApprovalRequest(initialRequest)
      : approval.data;
  const approveMutation = useApproveWorkRequestMutation();
  const declineMutation = useDeclineWorkRequestMutation();
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const [declineError, setDeclineError] = useState<string | null>(null);
  const [procedureError, setProcedureError] = useState<string | null>(null);
  const [isProcedureAttachmentPending, setIsProcedureAttachmentPending] =
    useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priorityCode, setPriorityCode] = useState("");
  const [workTypeCode, setWorkTypeCode] = useState("REACTIVE");
  const [assignees, setAssignees] = useState<string[]>([]);
  const [estimatedHours, setEstimatedHours] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const { procedure, setProcedure, hasProcedureChanges } =
    useDraftProcedureSelection({
      open,
      draftKey: `${procedureQuery.scopeKey}:${requestId ?? ""}`,
      data: procedureQuery.data,
    });
  const [selectedAssetId, setSelectedAssetId] = useState<AssetId | null>(null);
  const titleValue = title || approvalData?.title || "";
  const descriptionValue = description || approvalData?.description || "";
  const priorityValue =
    priorityCode || approvalData?.severity?.toUpperCase() || "MEDIUM";
  const canApprove = Boolean(
    approvalData &&
      approvalData.convertedWorkOrderId === null &&
      titleValue.trim() &&
      descriptionValue.trim(),
  );
  const canRetryProcedureAttachment = Boolean(
    approveMutation.data?.workRequestId === requestId && procedure !== "NONE",
  );
  const approvalAssetId =
    approvalData && approvalData.asset.id > 0
      ? findAssetIdByTreeId(
          assetTreeQuery.data?.nodes ?? [],
          approvalData.asset.id,
        )
      : null;
  const draftAssetId =
    selectedAssetId ?? initialRequest?.asset?.publicId ?? approvalAssetId;
  const guard = useUnsavedChangesGuard({
    hasUnsavedChanges:
      open &&
      (titleValue !== (approvalData?.title ?? "") ||
        descriptionValue !== (approvalData?.description ?? "") ||
        priorityValue !== (approvalData?.severity?.toUpperCase() ?? "MEDIUM") ||
        workTypeCode !== "REACTIVE" ||
        assignees.length > 0 ||
        estimatedHours !== "" ||
        startDate !== "" ||
        dueDate !== "" ||
        draftAssetId !== (initialRequest?.asset?.publicId ?? approvalAssetId) ||
        hasProcedureChanges),
  });
  function requestClose() {
    guard.requestLeave(onClose);
  }
  const draftAsset = flattenAssetTree(assetTreeQuery.data?.nodes ?? []).find(
    (row) => row.assetId === draftAssetId,
  );
  const pickedAsset = pickerQuery.data?.options.find(
    (item) => item.id === selectedAssetId,
  );
  const formApproval =
    selectedAssetId && pickedAsset && approvalData
      ? {
          ...approvalData,
          asset: {
            ...approvalData.asset,
            id: draftAsset?.id ?? approvalData.asset.id,
            name: pickedAsset.name,
            locationName: getAssetPickerLocationName(
              pickerQuery.data?.options ?? [],
              selectedAssetId,
              assetTreeQuery.data?.nodes ?? [],
            ),
          },
        }
      : approvalData;
  const procedureOptions = getProcedureOptions(
    procedureQuery.data?.items,
    procedureQuery.data?.defaultProcedure,
  );
  const assetLink: ApprovalAssetLink | null =
    draftAssetId && selectedCustomer && selectedFacility
      ? {
          assetId: draftAssetId,
          search: {
            ...getWorkScopeSearch(selectedCustomer, selectedFacility),
            ...(requestId
              ? toOriginSearch({
                  type: "request",
                  id: requestId,
                  search: originListSearch,
                })
              : {}),
          },
        }
      : null;
  const resetDetails = () => {
    if (approvalData) {
      setTitle(approvalData.title);
      setDescription(approvalData.description);
      setPriorityCode(approvalData.severity?.toUpperCase() ?? "MEDIUM");
      setSelectedAssetId(null);
    }
  };
  const submitApproval = async () => {
    if (!requestId || !selectedCustomer || !selectedFacility) return;
    setProcedureError(null);
    const hours = estimatedHours.trim() ? Number(estimatedHours) : null;
    if (hours !== null && (!Number.isFinite(hours) || hours < 0)) {
      setProcedureError("Estimated hours must be zero or greater.");
      return;
    }
    const parsedAssignees = assignees.map(parseAssigneeValue);
    try {
      const approvalResult =
        approveMutation.data?.workRequestId === requestId
          ? approveMutation.data
          : await approveMutation.mutateAsync({
              workRequestId: requestId,
              title: titleValue,
              description: descriptionValue,
              priorityCode: priorityValue as RequestPriority,
              confirmDuplicateWarning: false,
              assetId: selectedAssetId ?? undefined,
              workTypeCode,
              assignedUserIds: parsedAssignees
                .filter(
                  (target): target is { userId: number } => "userId" in target,
                )
                .map((target) => target.userId),
              assignedTeamIds: parsedAssignees
                .filter(
                  (target): target is { teamId: number } => "teamId" in target,
                )
                .map((target) => target.teamId),
              basicStartDate: startDate || null,
              basicFinishDate: dueDate || null,
              estimatedMinutes: hours === null ? null : Math.round(hours * 60),
              vendorName: null,
            });
      if (procedure !== "NONE") {
        setIsProcedureAttachmentPending(true);
        try {
          await attachProcedureToWorkOrder({
            customerId: selectedCustomer.id,
            facilityId: selectedFacility.id,
            workOrderId: workOrderIdSchema.parse(approvalResult.workOrderId),
            procedureId: procedure,
          });
        } finally {
          setIsProcedureAttachmentPending(false);
        }
      }
      guard.allowNavigation();
      onClose();
    } catch (error) {
      setProcedureError(
        error instanceof Error
          ? error.message
          : "Unable to attach the selected procedure.",
      );
    }
  };
  function handleDeclineOpenChange(nextOpen: boolean) {
    if (!nextOpen && !declineMutation.isPending) {
      setDeclineError(null);
    }
    setIsDeclineOpen(nextOpen);
  }
  async function handleDecline(input: DeclineRequestDialogInput) {
    if (!requestId) return;
    try {
      await declineMutation.mutateAsync({
        ...input,
        requestNumber: initialRequest?.requestNumber ?? "Work Request",
        workRequestId: requestId,
      });
      setDeclineError(null);
      setIsDeclineOpen(false);
    } catch (error) {
      setDeclineError(
        error instanceof Error
          ? error.message
          : "Unable to decline this request. Please try again.",
      );
    }
  }
  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(nextOpen: boolean) => {
          if (!nextOpen) requestClose();
        }}
      >
        <DrawerContent size="default">
          <DrawerHeader
            title={titleValue || "Create Work Order"}
            subtitle={
              requestId
                ? `CREATE WORK ORDER  -  FROM ${initialRequest?.requestNumber ?? "Work Request"}`
                : "CREATE WORK ORDER"
            }
            closeLabel="Close approval sidesheet"
          />
          <DrawerBody>
            <Stack gap={16}>
              {!approvalData && approval.isError ? (
                <Alert
                  tone="negative"
                  title="Work request could not be loaded."
                />
              ) : null}
              {approvalData ? (
                <>
                  <ApproveWorkRequestSummary
                    approval={formApproval ?? approvalData}
                    assetLink={assetLink}
                  />
                  {approvalData.convertedWorkOrderId ? (
                    <Alert
                      tone="positive"
                      title={`Already converted to Work Order ${approvalData.convertedWorkOrderId}.`}
                    />
                  ) : null}
                  <ApproveWorkOrderForm
                    approval={formApproval ?? approvalData}
                    selectedAssetId={draftAssetId}
                    onPublicAssetChange={setSelectedAssetId}
                    showVendor={false}
                    lookups={lookupData}
                    teamsUsers={teamsUsersData}
                    procedures={procedureOptions}
                    onRetryProcedures={() => void procedureQuery.refetch()}
                    procedureStatus={
                      procedureQuery.isPending
                        ? "loading"
                        : procedureQuery.isError
                          ? "error"
                          : "ready"
                    }
                    readonlyDetails={canRetryProcedureAttachment}
                    procedureDisabled={
                      approveMutation.isPending || isProcedureAttachmentPending
                    }
                    title={titleValue}
                    description={descriptionValue}
                    priority={priorityValue}
                    workType={workTypeCode}
                    assignees={assignees}
                    estimatedHours={estimatedHours}
                    startDate={startDate}
                    dueDate={dueDate}
                    procedure={procedure}
                    vendor=""
                    onTitleChange={setTitle}
                    onDescriptionChange={setDescription}
                    onPriorityChange={setPriorityCode}
                    onWorkTypeChange={setWorkTypeCode}
                    onAssigneesChange={setAssignees}
                    onEstimatedHoursChange={setEstimatedHours}
                    onStartDateChange={setStartDate}
                    onDueDateChange={setDueDate}
                    onProcedureChange={(value) => {
                      setProcedure(value);
                      setProcedureError(null);
                    }}
                    onVendorChange={() => {}}
                    onReset={resetDetails}
                  />
                  {approveMutation.isSuccess ? (
                    <Alert
                      tone="positive"
                      title={`Created Work Order ${approveMutation.data.workOrderNumber}.`}
                    />
                  ) : null}
                  {procedureError ? (
                    <Alert tone="negative" title={procedureError} />
                  ) : null}
                </>
              ) : null}
              {declineMutation.isSuccess ? (
                <Alert tone="informative" title="Work request declined." />
              ) : null}
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <div className="flex w-full items-center justify-between gap-(--spacing-10)">
              <Button
                variant="ghost"
                disabled={!requestId || declineMutation.isPending}
                onClick={() => {
                  setDeclineError(null);
                  setIsDeclineOpen(true);
                }}
              >
                Decline request
              </Button>
              <Group>
                <Button variant="outline" onClick={requestClose}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    ((!canApprove || !procedureQuery.data) &&
                      !canRetryProcedureAttachment) ||
                    approveMutation.isPending ||
                    isProcedureAttachmentPending
                  }
                  loading={
                    approveMutation.isPending || isProcedureAttachmentPending
                  }
                  onClick={() => void submitApproval()}
                >
                  {canRetryProcedureAttachment
                    ? "Retry procedure attachment"
                    : "Approve & convert"}
                </Button>
              </Group>
            </div>
          </DrawerFooter>
        </DrawerContent>
        {requestId ? (
          <DeclineRequestDialog
            error={declineError}
            isPending={declineMutation.isPending}
            onConfirm={(input) => void handleDecline(input)}
            onOpenChange={handleDeclineOpenChange}
            open={isDeclineOpen}
            requestNumber={initialRequest?.requestNumber ?? "Work Request"}
          />
        ) : null}
      </Drawer>
      <UnsavedChangesDialog {...guard} />
    </>
  );
}
